import { gql } from '@apollo/client'
import { DocumentGapDate } from 'share/domain/document'

export const GET_DEFAULT_DOCUMENT_CATEGORYID = gql`
  query GET_DEFAULT_DOCUMENT_CATEGORYID {
    documents {
      defaultCategoryDocumentFolder {
        categoryId
        categoryName
        bccReferenceCategoryId
      }
    }
  }
`

export interface DefaultCategoryFolder {
  categoryId: number
  categoryName: string
  bccReferenceCategoryId: number | null
}

export interface GetDefaultDocumentCategoryIdResponse {
  documents: {
    defaultCategoryDocumentFolder: DefaultCategoryFolder
  }
}

import {
  DocumentCategory,
  Document,
  DocumentComment,
  DocumentReview,
  ManualUploadCertData,
  DocumentRequestChecklistItem,
  CheckListDocument
} from 'share/domain/document'

export const GET_DOCUMENT_DASHBOARD = gql`
  query GET_DOCUMENT_DASHBOARD($caseId: Int!) {
    documents {
      documentDashboard(caseId: $caseId) {
        submit {
          id
          name
          description
          subcategoryList
          status
        }
        review {
          id
          name
          status
          toReview
          uploaded
          opened
          comments {
            id
            attorneyName
            attorneyAvatarUrl
            comment
            created
          }
        }
      }
    }
  }
`

export interface GetDocumentDashboardQueryVariables {
  caseId: number
}

export interface GetDocumentDashboardQueryResponse {
  documents: {
    documentDashboard: {
      submit: DocumentCategory[]
      review: DocumentReview[]
    }
  }
}

export const GET_REVIEW_DOCUMENTS = gql`
  query DOCUMENTS_TO_REVIEW($caseId: Int!) {
    documents {
      documentsToReview(caseId: $caseId) {
        id
        name
        status
        toReview
        uploaded
        opened
        comments {
          id
          attorneyName
          attorneyAvatarUrl
          comment
          created
        }
      }
    }
  }
`

export interface GetReviewDocumentsResponse {
  documents: {
    documentsToReview: DocumentReview[]
  }
}

export const GET_CHILD_FOLDERS_FOR_PARENT_FOLDER_QUERY = gql`
  query GET_CHILD_FOLDERS_FOR_PARENT_FOLDER($caseId: Int!, $categoryId: Int!) {
    documents {
      childFoldersForParentFolder(caseId: $caseId, categoryId: $categoryId) {
        name
        subCategories {
          id
          name
          description
          status
          subcategoryList
        }
      }
    }
  }
`

export interface GetChildFoldersForParentFolderQueryResponse {
  documents: {
    childFoldersForParentFolder: {
      name: string
      subCategories: DocumentCategory[]
    }
  }
}

export interface GetChildFoldersForParentFolderQueryVariables {
  categoryId: number
  caseId?: number
}

export const GET_DOCUMENTS_CHILD_LEAF_CATEGORY_QUERY = gql`
  query GET_DOCUMENTS_CHILD_LEAF_CATEGORY($caseId: Int!, $categoryId: Int!) {
    documents {
      documentsChildLeafCategory(caseId: $caseId, categoryId: $categoryId) {
        name
        documentList {
          id
          name
          status
          uploaded
          comments {
            id
            attorneyName
            attorneyAvatarUrl
            comment
            created
          }
        }
      }
    }
  }
`

export interface GetDocumentsChildLeafCategoryQueryResponse {
  documents: {
    documentsChildLeafCategory: {
      name: string
      documentList: Document[]
    }
  }
}

export interface GetDocumentsChildLeafCategoryQueryVariables {
  caseId: number
  categoryId: number
}

export const GET_DOCUMENT_DETAIL_QUERY = gql`
  query GET_DOCUMENT_DETAIL_QUERY($documentId: Int!) {
    documents {
      get(documentId: $documentId) {
        categories {
          subCategories {
            documents {
              comments {
                id
                attorneyName
                attorneyAvatarUrl
                comment
                created
              }
            }
          }
        }
      }
    }
  }
`

export interface GetDocumentsDetailQueryResponse {
  documents: {
    get: {
      categories: [
        { subCategories: [{ documents: [{ comments: DocumentComment[] }] }] }
      ]
    }
  }
}

export interface GetDocumentsDetailQueryVariables {
  documentId: number
}

// Get documents pending
export const GET_PENDING_DOCUMENTS = gql`
  query GET_PENDING_DOCUMENTS($caseId: Int!) {
    documents {
      documentsToReview(caseId: $caseId) {
        id
        status
      }
    }
  }
`

export interface GetPendingDocumentsResponse {
  documents: {
    documentsToReview: [
      {
        id: string
        status: string
      }
    ]
  }
}

export const GET_DOCUMENTS_BY_CASE_AND_STATUS = gql`
  query GET_DOCUMENTS_BY_CASE_AND_STATUS($caseId: Int!, $status: String!) {
    documents {
      get(caseId: $caseId, documentStatus: $status) {
        categories {
          subCategories {
            documents {
              id
              name
              status
            }
          }
        }
      }
    }
  }
`
export interface GetDocumentsResponse {
  documents: {
    get: { categories: [{ subCategories: [{ documents: Document[] }] }] }
  }
}

export interface GetDocumentsByCaseAndStatusVariables {
  caseId: number
  status: string
}

export const GET_MANUAL_UPLOAD_CERT_DATA = gql`
  query GET_MANUAL_UPLOAD_CERT_DATA($caseId: Int!, $courseType: Int!) {
    documents {
      manualUploadCertificateData(caseId: $caseId, courseType: $courseType) {
        generalDocumentId
        generalDocumentName
        generalDocumentDescription
        generalDocumentStatus
        generalDocumentSubcategoryList
        certificateDocumentId
        certificateDocumentName
        certificateDocumentDescription
        certificateDocumentStatus
        certificateDocumentSubcategoryList
      }
    }
  }
`
export interface GetManualUploadCertDataResponse {
  documents: {
    manualUploadCertificateData: ManualUploadCertData
  }
}

export interface GetManualUploadCertDataVariables {
  caseId: number
  courseType: number
}

////

export const GET_DOCUMENT_CHECKLIST_ITEMS = gql`
  query GET_DOCUMENT_CHECKLIST_ITEMS($caseId: Int!) {
    documents {
      checkListItems(caseId: $caseId) {
        id
        name
        status
        documentCount
        comment
        hasWarnings
        durationDescription
        documentItemTypeId
      }
    }
  }
`

export interface GetDocumentChecklistItemsQueryVariables {
  caseId: number
}

export interface GetDocumentCheckListItemsQueryResponse {
  documents: {
    checkListItems: DocumentRequestChecklistItem[]
  }
}

export const GET_CHECKLISTITEM_DOCUMENTS = gql`
  query GET_CHECKLISTITEM_DOCUMENTS(
    $request: GetChecklistItemDocRequestDtoInput!
  ) {
    documents {
      checkListItemDocuments(request: $request) {
        name
        documentList {
          matterDocumentChecklistItemDocumentId
          matterCheckListItemId
          matterDocumentId
          clientPortalDocumentId
          name
          status
          uploaded
          comments
        }
      }
    }
  }
`

export interface GetChecklistItemDocumentsQueryVariables {
  request: {
    caseId: number
    documentCheckListItemId: number
    documentCheckListItemName: string | null
    documentCheckListItemStatus: string | null
  }
}

export interface GetChecklistItemDocumentsQueryResponse {
  documents: {
    checkListItemDocuments: {
      name: string
      documentList: CheckListDocument[]
    }
  }
}

export const GET_AIDOCUMENT_DASHBOARD = gql`
  query GET_AIDOCUMENT_DASHBOARD($caseId: Int!) {
    documents {
      aiDocumentDashboard(caseId: $caseId) {
        checkList {
          id
          name
          status
          documentCount
          comment
          hasWarnings
          durationDescription
          documentItemTypeId
        }
        review {
          id
          name
          status
          toReview
          uploaded
          opened
          comments {
            id
            attorneyName
            attorneyAvatarUrl
            comment
            created
          }
        }
      }
    }
  }
`

export interface GetAiDocumentDashboardQueryVariables {
  caseId: number
}

export interface GetAiDocumentDashboardQueryResponse {
  documents: {
    aiDocumentDashboard: {
      checkList: DocumentRequestChecklistItem[]
      review: DocumentReview[]
    }
  }
}

export const GET_DOCUMENT_CHECKLIST_GAPS = gql`
  query GET_DOCUMENT_CHECKLIST_GAPS(
    $request: GetDocumentChecklistDocumentGapsRequestDtoInput!
  ) {
    documents {
      documentChecklistDocumentGaps(request: $request) {
        success
        errorMessage
        groupHeaderName
        hasWarnings
        hasTotalsSubtractionWarnings
        documentChecklistItemDates {
          gapType
          name
          group
          startDate
          endDate
          message
        }
      }
    }
  }
`

export interface GetDocumentChecklistGapsQueryVariables {
  caseId: number
  documentCheckListItemId: number
}

export interface GetDocumentChecklistGapsQueryResponse {
  documents: {
    documentChecklistDocumentGaps: {
      success: boolean
      errorMessage: string | null
      documentChecklistItemDates: DocumentGapDate[]
      groupHeaderName: string | null
      hasWarnings: boolean
      hasTotalsSubtractionWarnings: boolean
    }
  }
}
